import { Map } from 'immutable'
import { get, groupBy, isEmpty } from 'lodash-es'
import { DateTime, Duration } from 'luxon'
import React, {
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { InView, useInView } from 'react-intersection-observer'
import { LuCodesandbox, LuNavigation2, LuNavigation2Off } from 'react-icons/lu'

import { useSettings } from '@/feature/organizations/api/useSettings'
import { EtaLocationType } from '@/feature/eta'
import { FeatureFlag, useFlag, WhenFlag } from '@/feature/feature-flags'
import { reverseGeocode } from '@/feature/map'
import { getLongitudeLatitudeFromGeoFence } from '@/feature/map/utils/getLongitudeLatitudeFromGeoFence'
import {
  AsideList,
  AsideListItem,
  AsideListProps,
  ObservatoryContext,
  SearchSection,
  useMarkerColors,
  usePersistentObservatoryPageStore,
} from '@/feature/observatory'
import { AssetTrackingData, useAssetTelemetry } from '@/feature/telemetry'
import { TableHeaderTags, TagsFilterPopout } from '@/feature/tags'
import { ASSET_SORT_OPTIONS } from '@/feature/observatory/filters'

import { MarkerBadge } from '@/components/MarkerBadge'

import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { Loader } from '@/lib/core/components/Loader'

import { useAllAssets } from '@/lib/features/Assets/api'
import { Asset, AssetContentType } from '@/lib/features/Assets/interfaces'

import { formatAsDuration } from '@/lib/utils/date-time'

import styles from './AssetsAside.module.css'

const AssetListItemBindingRef = (
  {
    assetId,
    ownerImage,
    description,
    reference,
    plateNumber,
    assetTrackingData,
    isPTU,
    isActive,
    onClick,
  }: {
    assetId: number
    ownerImage?: string
    description: string
    reference: string
    plateNumber: string
    assetTrackingData: AssetTrackingData | undefined
    isPTU: boolean
    isActive?: boolean
    onClick: () => void
  },
  ref: ForwardedRef<HTMLLIElement>,
) => {
  const selectedItem = usePersistentObservatoryPageStore(({ selectedItem }) => selectedItem)
  const setSelectedItem = usePersistentObservatoryPageStore(
    ({ setSelectedItem }) => setSelectedItem,
  )
  const isSelectedAnAsset = selectedItem?.type === 'geoFence'

  const { geoFences } = useContext(ObservatoryContext)

  const isSelectedGeoFence = useMemo(() => {
    return geoFences.data.results.find((geoFence) => geoFence.id === selectedItem?.id)
  }, [geoFences])

  const canShowEta = get(isSelectedGeoFence, 'geoJson.geometry.type') !== 'LineString'

  const heading = assetTrackingData?.heading ?? 0
  const lastSeen = assetTrackingData?.sensorDatetime
  const speed = assetTrackingData?.speed ?? 0
  const isMoving = assetTrackingData?.isMoving ?? false

  const { moving, stationary, lostContactDefault, lostContactAfterOfflineTimeout } =
    useMarkerColors()

  const color = isMoving ? moving : stationary

  const setEtaLocationFrom = usePersistentObservatoryPageStore(({ eta }) => eta.setEtaLocationFrom)
  const setEtaLocationTo = usePersistentObservatoryPageStore(({ eta }) => eta.setEtaLocationTo)
  const setShowEta = usePersistentObservatoryPageStore(({ eta }) => eta.setShowEta)

  const searchLocation = usePersistentObservatoryPageStore((state) => state.searchLocation)
  const setSearchLocation = usePersistentObservatoryPageStore((state) => state.setSearchLocation)

  const { data: settings } = useSettings()

  const hasLostContact = useMemo(
    () =>
      !lastSeen ||
      DateTime.fromJSDate(lastSeen).diffNow() <
        Duration.fromObject({ minutes: -(settings?.vehicleOfflineAfter ?? 24 * 60) }),
    [lastSeen],
  )

  const lostContactColor =
    settings?.vehicleOfflineAfter == null ? lostContactDefault : lostContactAfterOfflineTimeout

  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)

  const etaAssetsOnClick = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (assetTrackingData) {
        const currentLocation = await reverseGeocode(
          assetTrackingData.lat,
          assetTrackingData.lon,
          reverseGeocodeMode,
        )
        if (currentLocation === undefined) {
          return
        }
        if (isSelectedGeoFence !== undefined) {
          const { lng, lat } = getLongitudeLatitudeFromGeoFence(isSelectedGeoFence)
          setEtaLocationFrom({
            id: isSelectedGeoFence.id,
            title: isSelectedGeoFence.title,
            latitude: lat,
            longitude: lng,
            address: isSelectedGeoFence.address,
            highRiskArea: isSelectedGeoFence.highRiskArea,
            type: EtaLocationType.GEOFENCE,
          })
        } else if (searchLocation !== undefined) {
          setEtaLocationFrom({
            latitude: searchLocation.location.lat,
            longitude: searchLocation.location.lng,
            address: searchLocation.address,
            type: EtaLocationType.CUSTOM,
          })
        }
        setSelectedItem(undefined)
        setEtaLocationTo({
          id: assetId,
          title: description,
          plateNumber: plateNumber,
          latitude: assetTrackingData.latitude,
          longitude: assetTrackingData.longitude,
          speed: speed,
          heading: heading,
          address: currentLocation,
          type: EtaLocationType.ASSET,
          avatar: ownerImage,
        })
        setSearchLocation(undefined)
        setShowEta(true)
      }
    },
    [
      assetTrackingData,
      description,
      heading,
      isSelectedGeoFence,
      ownerImage,
      plateNumber,
      searchLocation,
    ],
  ) as unknown as MouseEventHandler<HTMLDivElement>

  return (
    <AsideListItem
      className={styles.assetListItem}
      onClick={onClick}
      active={isActive}
      header={
        <div className={styles.assetTopLine}>
          <CircleAvatar src={ownerImage} size={32} withBorder />
          <span className={styles.reference} title={reference}>
            {reference}
          </span>
          <span className={styles.plateNumber} title={plateNumber}>
            {plateNumber}
          </span>
        </div>
      }
      footer={
        <InView>
          {({ inView, ref }) => (
            <div className={styles.assetFooter} ref={ref}>
              <div className={styles.assetBottomLine}>
                Last Seen: {inView ? formatAsDuration(lastSeen) : '-'}
              </div>
            </div>
          )}
        </InView>
      }
      customAction={
        <FeatureFlag flag={'featureEta'}>
          <WhenFlag>
            {(searchLocation !== undefined || (isSelectedAnAsset && canShowEta)) &&
              assetTrackingData && (
                <div
                  role={'button'}
                  onClick={etaAssetsOnClick}
                  className={[styles.assetEtaButton, isActive ? styles.active : ''].join(' ')}
                >
                  ETA
                </div>
              )}
          </WhenFlag>
        </FeatureFlag>
      }
      icon={
        <InView>
          {({ inView, ref }) => (
            <div className={styles.assetIcon} ref={ref}>
              <span className={isPTU ? styles.ptuPill : styles.gone}>PTU</span>
              {/*
                A tracking unit always shows its device glyph; only the colour tells you it
                has lost contact. A vehicle swaps its arrow for the struck-through one.
              */}
              {isPTU ? (
                <MarkerBadge
                  glyph={LuCodesandbox}
                  color={hasLostContact ? lostContactColor : color}
                  size={25}
                />
              ) : hasLostContact ? (
                <MarkerBadge glyph={LuNavigation2Off} size={25} color={lostContactColor} />
              ) : (
                <MarkerBadge
                  glyph={LuNavigation2}
                  color={color}
                  size={25}
                  rotate={inView ? heading : 0}
                  filled
                />
              )}
            </div>
          )}
        </InView>
      }
      ref={ref}
    />
  )
}

export const AssetListItemBinding = forwardRef(AssetListItemBindingRef)

/** Inner component that subscribes to telemetry - only mounted when visible or active */
const AssetListItemWithTelemetry = ({
  asset,
  isActive,
  onClick,
  setRef,
}: {
  asset: Asset
  isActive: boolean
  onClick: () => void
  setRef: (node: HTMLLIElement | null) => void
}) => {
  const assetTrackingData = useAssetTelemetry(asset.vehicleBridgeId)
  const reference = isEmpty(asset.reference) ? asset.description : asset.reference

  return (
    <AssetListItemBinding
      ref={setRef}
      assetId={asset.id}
      isActive={isActive}
      ownerImage={asset.owner.avatar}
      reference={reference}
      description={asset.description}
      assetTrackingData={assetTrackingData}
      plateNumber={asset.plateNumber}
      isPTU={asset.contentType === AssetContentType.PTU}
      onClick={onClick}
    />
  )
}

export const AssetListItem = ({
  asset,
  activeAsset,
  onAssetSelected,
}: {
  asset: Asset
  activeAsset?: Asset
  onAssetSelected: (asset: Asset) => void
}) => {
  const isActive = activeAsset?.id == asset.id
  const { ref: inViewRef, inView } = useInView()
  const baseElement = useRef<HTMLLIElement>(null)

  // Combine refs for both InView observation and scroll-into-view
  const setRefs = useCallback(
    (node: HTMLLIElement | null) => {
      baseElement.current = node
      inViewRef(node)
    },
    [inViewRef],
  )

  useEffect(() => {
    if (isActive && baseElement.current) {
      baseElement.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [isActive])

  const onClick = useCallback(() => {
    onAssetSelected(asset)
  }, [onAssetSelected, asset])

  // Only subscribe to telemetry when in view or active
  if (inView || isActive) {
    return (
      <AssetListItemWithTelemetry
        asset={asset}
        isActive={isActive}
        onClick={onClick}
        setRef={setRefs}
      />
    )
  }

  // Render placeholder without telemetry subscription
  const reference = isEmpty(asset.reference) ? asset.description : asset.reference
  return (
    <AssetListItemBinding
      ref={setRefs}
      assetId={asset.id}
      isActive={false}
      ownerImage={asset.owner.avatar}
      reference={reference}
      description={asset.description}
      assetTrackingData={undefined}
      plateNumber={asset.plateNumber}
      isPTU={asset.contentType === AssetContentType.PTU}
      onClick={onClick}
    />
  )
}

// FIXME: switch to use plain sort() when we have access to the creation date from the backend
const sortingFunction =
  (type: string, telemetryData: Map<string, AssetTrackingData>) => (a: Asset, b: Asset) => {
    switch (type) {
      case 'Alphabetically':
      case 'Alphabetical Active': {
        const aName = isEmpty(a.reference) ? a.description : a.reference
        const bName = isEmpty(b.reference) ? b.description : b.reference

        return aName.localeCompare(bName)
      }
      case 'Date Created':
        // FIXME: This is not exposed in the API, assuming the backend is returning in insertion order
        return 0
      case 'Recently Active': {
        const aAsset = telemetryData.get(a.vehicleBridgeId)
        const bAsset = telemetryData.get(b.vehicleBridgeId)

        const aTime = aAsset?.sensorDatetime?.getTime() ?? 0
        const bTime = bAsset?.sensorDatetime?.getTime() ?? 0

        return bTime - aTime
      }
      default:
        return 0
    }
  }

const AssetList = ({
  activeAsset,
  onAssetSelected,
  assets,
}: {
  assets?: Asset[]
  activeAsset?: Asset
  onAssetSelected: (asset: Asset) => void
}) => {
  const render: AsideListProps<Asset>['children'] = useCallback(
    (asset: Asset) => (
      <AssetListItem asset={asset} activeAsset={activeAsset} onAssetSelected={onAssetSelected} />
    ),
    [activeAsset, onAssetSelected],
  )
  const keyExtractor = useCallback((asset: Asset) => asset.id, [])
  return (
    <AsideList<Asset> items={assets} keyExtractor={keyExtractor}>
      {render}
    </AsideList>
  )
}

export type AssetsAsideProps = {
  activeAsset?: Asset
  onAssetSelected: (asset: Asset) => void
  assets: ReturnType<typeof useAllAssets>
}

export const AssetsAside = ({ activeAsset, onAssetSelected, assets }: AssetsAsideProps) => {
  const assetsFilters = usePersistentObservatoryPageStore((state) => state.assetsFilters)
  const setAssetsFilters = usePersistentObservatoryPageStore((state) => state.setAssetsFilters)
  const assetsSearch = usePersistentObservatoryPageStore((state) => state.assetsSearch)
  const setAssetsSearch = usePersistentObservatoryPageStore((state) => state.setAssetsSearch)

  const { data: settings } = useSettings()

  const telemetryData = useAssetTelemetry()

  const sortedAssets = useMemo(() => {
    let sorted: Asset[] = assets.data?.results ?? []
    if (assetsFilters.toggles['Offline Only']) {
      sorted = sorted.filter((asset) => {
        // we consider inactive "offline" here as well
        const sensorDatetime = telemetryData.get(asset.vehicleBridgeId)?.sensorDatetime
        return (
          !sensorDatetime ||
          DateTime.fromJSDate(sensorDatetime).diffNow() <
            Duration.fromObject({ minutes: -(settings?.vehicleOfflineAfter ?? 24 * 60) })
        )
      })
    }
    if (assetsFilters.sortOn === 'Alphabetical Active') {
      const groupedAssets = groupBy(sorted, (asset) => {
        const assetTelemetryData = telemetryData.get(asset.vehicleBridgeId)
        if (assetTelemetryData == undefined) {
          return 'Offline'
        } else if (
          DateTime.fromJSDate(assetTelemetryData.sourceDatetime).diffNow() <
          Duration.fromObject({ hours: -24 })
        ) {
          return 'Inactive'
        } else {
          return 'Active'
        }
      })
      const sortFunction = sortingFunction('Alphabetical Active', telemetryData)
      sorted = [
        ...(groupedAssets.Active?.slice().sort(sortFunction) ?? []),
        ...(groupedAssets.Inactive?.slice().sort(sortFunction) ?? []),
        ...(groupedAssets.Offline?.slice().sort(sortFunction) ?? []),
      ]
    } else {
      sorted = sorted?.slice().sort(sortingFunction(assetsFilters.sortOn, telemetryData))
    }

    if (assetsFilters.sortDirection === 'asc') {
      sorted = sorted.reverse()
    }

    return sorted
  }, [
    assets.data?.results,
    assetsFilters.sortOn,
    assetsFilters.sortDirection,
    assetsFilters.toggles['Offline Only'],
    settings?.vehicleOfflineAfter,
    ['Recently Active', 'Alphabetical Active'].includes(assetsFilters.sortOn) ||
    assetsFilters.toggles['Offline Only']
      ? telemetryData
      : null,
  ])

  const assetRender = useMemo(() => {
    const areAllFiltersDisabled = () => {
      return (
        assetsFilters.toggles['PTU'] === false &&
        assetsFilters.toggles['My Assets'] === false &&
        assetsFilters.toggles['Shared With Me'] === false
      )
    }

    if (assets.error) {
      return <div className={styles.emptyItems}>Failed to load assets, please try again</div>
    } else if (assets.isLoading) {
      return (
        <div className={styles.emptyItems}>
          <Loader />
        </div>
      )
    } else if (isEmpty(sortedAssets)) {
      return (
        <div className={styles.emptyItems}>
          <p>
            This filter has no results
            {areAllFiltersDisabled() ? (
              <>
                <br />
                Enable PTU, My Assets or Shared With Me
              </>
            ) : null}
          </p>
        </div>
      )
    } else {
      return (
        <AssetList
          activeAsset={activeAsset}
          onAssetSelected={onAssetSelected}
          assets={sortedAssets}
        />
      )
    }
  }, [
    assets.error,
    assets.isLoading,
    sortedAssets,
    assetsFilters.toggles,
    activeAsset,
    onAssetSelected,
  ])

  return (
    <aside className={styles.assetsAside}>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>Assets</h1>
      </div>
      <SearchSection
        search={assetsSearch}
        onSearchChanged={setAssetsSearch}
        filters={assetsFilters}
        onFilterChanged={setAssetsFilters}
        sortOptions={ASSET_SORT_OPTIONS.filter((o) => o !== 'Alphabetical Active')}
      />
      <hr className={styles.searchDivider} />

      <FeatureFlag flag="assetTags">
        <WhenFlag>
          <div style={{ padding: '0 1.5rem' }}>
            <TableHeaderTags shouldDisplay displayTags={false} />
          </div>
          <TagsFilterPopout />
        </WhenFlag>
      </FeatureFlag>

      {assetRender}
    </aside>
  )
}
